import React, { FC } from 'react';
import Link from 'next/link';
import { Link as ScrollLink } from 'react-scroll';
import { useRouter } from 'next/router';
import Icon from '@common/assets';
import { PositionCompanyOffsetName } from '@components/position/jd/positionCompany';
import { useChatCompanyByCompanySn } from '@repository/chat/useChat';
import { useUserInfo } from '@repository/auth/useUserInfo';
import LoginModal from '@components/loginModal';
import { useMatchingCompany } from '@repository/position/usePosition';
import { PositionDetailVO } from '@domain/vo/position/jd/PositionDetailVO';
import { V2Route } from '@utils/routeUtil';
import NumberFormat from '@utils/number/format';
import useModal from '@utils/hooks/useModal';
import classnames from 'classnames/bind';
import styles from './companyChannel.module.scss';

const cx = classnames.bind(styles);

interface CompanyChannelProps {
  positionSn: number;
  positionDetail: PositionDetailVO;
}

const CompanyChannel: FC<CompanyChannelProps> = ({ positionSn, positionDetail }) => {
  const router = useRouter();
  const { data: userData, refetch } = useUserInfo();
  const isLogin = !!userData?.id;
  const { companyInfo } = positionDetail;
  const { data: chatData } = useChatCompanyByCompanySn(companyInfo.companySn);
  const presentCompanySn = companyInfo.companySn;
  const { data: companyPositionList } = useMatchingCompany(positionSn);
  const companyUrl = V2Route.getCompanyChannelPage(presentCompanySn);
  const positionCount = NumberFormat.formatPositionCountInCompany(Number(companyPositionList?.positions?.length ?? 0));
  const { isModalOpen, openModal, closeModal } = useModal();

  if (!companyInfo.openYn) return null;

  const positionText =
    Number(positionCount) === 0 ? (
      <p className={cx('positionText')}>현재 1개 포지션 채용중</p>
    ) : (
      <ScrollLink
        to={PositionCompanyOffsetName}
        offset={-88}
        smooth={true}
        spy={true}
        role={'button'}
        className={cx('positionText', 'link')}
      >
        현재 {positionCount}개 포지션 채용중
      </ScrollLink>
    );

  const getRedirectUrl = () => {
    const url = window.location.href;
    if (url.includes('?')) {
      if (url.includes('channelId')) return '';
      return `${window.location.search}&channelId=${chatData?.channelId}`;
    }
    return `?channelId=${chatData?.channelId}`;
  };

  const handleChatClick = async () => {
    if (!isLogin) {
      openModal();
      return;
    }
    router.push(window.location.href + getRedirectUrl());
    refetch();
  };

  return (
    <div className={cx('companyChannelArea')}>
      <div className={cx('contentsArea')}>
        <em className={cx('title')}>{companyInfo.companyName}에 대한 더 자세한 정보를 알고싶으시다면?</em>
        {positionText}
      </div>
      <div className={cx('btnArea')}>
        <Link href={companyUrl} legacyBehavior>
          <a className={cx('channelBtn')} role="button">
            <Icon name="businessLight" width={24} height={24} />
            <span className={cx('text')}>기업 정보</span>
          </a>
        </Link>
        <button className={cx('channelBtn')} onClick={handleChatClick}>
          <Icon name="messageLight" width={24} height={24} />
          <span className={cx('text')}>기업 채팅</span>
        </button>
      </div>
      <LoginModal
        subTitle="채용정보 · 역량검사 · 매칭까지"
        title="취업기회 지금 바로 잡다!"
        visible={isModalOpen}
        onClose={closeModal}
      />
    </div>
  );
};

export default CompanyChannel;
